// Implements: F-CNV-009, F-UIX-008, F-UIX-009, F-UIX-010, F-UIX-017
/**
 * The inline editor of an earlier message of the person. Pure presentation.
 *
 * The text is a prop so a refused edit keeps what was typed (F-UIX-008); the refusal is the
 * server's own sentence for the codes it writes for people (busy, base version gone, not
 * editable). Nothing here says whether the new turn costs anything (OD-12). Ctrl+Enter (Cmd+Enter
 * on a Mac) resends, and the hint says so in words (F-UIX-017).
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { TURN_REFUSAL_CODES, userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ApiFailure } from "../../../types/errors";
import styles from "./MessageEditor.module.css";

/** The field's id: one editor exists at a time. */
export const EDITOR_FIELD_ID = "conversation-edit";

export interface MessageEditorProps {
  readonly text: string;
  readonly onTextChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
  /** A turn is open: nothing can be sent until it ends. */
  readonly disabled: boolean;
  /** The refusal of the last attempt, if any. */
  readonly error: ApiFailure | null;
}

export function MessageEditor({
  text,
  onTextChange,
  onSubmit,
  onCancel,
  disabled,
  error,
}: MessageEditorProps): ReactElement {
  const { t } = useTranslation();
  const blocked = disabled || text.trim() === "";
  const send = (): void => {
    if (!blocked) onSubmit();
  };
  const refusal =
    error === null ? undefined : userMessage(error, t("conversation.edit.failed"), TURN_REFUSAL_CODES);
  const hint = t("conversation.edit.hint");

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <Field id={EDITOR_FIELD_ID} label={t("conversation.edit.label")} hint={hint} error={refusal}>
        <Textarea
          id={EDITOR_FIELD_ID}
          value={text}
          onChange={onTextChange}
          onSubmitShortcut={send}
          disabled={disabled}
          invalid={refusal !== undefined}
          describedBy={describedBy(EDITOR_FIELD_ID, { hint, error: refusal })}
        />
      </Field>
      <div className={styles.actions}>
        <Button variant="primary" size="sm" type="submit" disabled={blocked}>
          {t("conversation.edit.submit")}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t("common.action.cancel")}
        </Button>
      </div>
    </form>
  );
}
